/**
 * @file schemas/contract-reviews.ts
 * @description 合約評審單 zod schema（ZHSR01-12）。
 *
 * 檢查項目按驗證方案（scheme）分版：TOP 12 項 / TAP 14 項（ECO 回落 TOP）。
 * 驗證組長提交時所有題目必須有 answer，
 * 題 7（特殊要求）與最後一項「其它」若選「是」必須填備注；
 * 總經理不受理時必須填 rejectionReason。
 */
import { z } from 'zod';
/**
 * 檢查項目清單——按驗證方案分版（客戶 PPT S19/S20/S21）。
 *
 * - TOP（12 項）：原有清單，第 2 條改為完整文案（配備足夠稽核員/技術專家…）。
 * - TAP（14 項）：基於 TOP，第 2 條同新文案；第 8 條加「一年內無終止紀錄 + 應上網查詢」；
 *   第 12 條改為「同時向多家驗證機構申請相同品項產品之驗證」；
 *   新增第 13 條「產銷履歷農產品主原料比例不得低於 50%」；
 *   原「其它」兜底項順延為第 14 條。
 * - ECO 無獨立表樣，取清單時回落 TOP（見 getContractReviewChecklist）。
 *
 * service 預填 / 前端展示均引用此常數，保證題序與 no 對應一致；
 * 存量單據的 checklist jsonb 已快照 label，渲染以存量為準，不受此處文案調整影響。
 */
export const CONTRACT_REVIEW_CHECKLISTS = {
    TOP: [
        '驗證類別是屬於 ZHCERT 被 TAF 認證業務範圍',
        '是否配備足夠的、有能力的稽核員或技術專家及其它資源，以保證公司有能力完成驗證活動？',
        '是否能滿足稽核時間、稽核地點、稽核使用語言',
        '是否具備申請驗證產品相關檢測標準之實驗室',
        '是否已解決與申請人之間在理解上的差異',
        '是否曾有過違規的情事',
        '是否能滿足申請業者的特殊要求？（是＝沒有特殊要求；否＝有特殊要求，請說明）',
        '是否曾向其他驗證機構申請驗證，或同時正向其他驗證機構提出申請',
        '驗證產品相關業務活動是否由業者（或授權代表）自行操作',
        '是否有委託其他人力與技術支援',
        '申請驗證是否有配合政府相關推動計畫或專案',
        '其它',
    ],
    TAP: [
        '驗證類別是屬於 ZHCERT 被 TAF 認證業務範圍',
        '是否配備足夠的、有能力的稽核員或技術專家及其它資源，以保證公司有能力完成驗證活動？',
        '是否能滿足稽核時間、稽核地點、稽核使用語言',
        '是否具備申請驗證產品相關檢測標準之實驗室',
        '是否已解決與申請人之間在理解上的差異',
        '是否曾有過違規的情事',
        '是否能滿足申請業者的特殊要求？（是＝沒有特殊要求；否＝有特殊要求，請說明）',
        '是否曾向其他驗證機構申請驗證，或同時正向其他驗證機構提出申請，且一年內沒有經驗證機構終止紀錄(若有不可歸責於農產品經營業者之事由，不再此限)；※應上網查詢。',
        '驗證產品相關業務活動是否由業者（或授權代表）自行操作',
        '是否有委託其他人力與技術支援',
        '申請驗證是否有配合政府相關推動計畫或專案',
        '是否同時向多家驗證機構申請相同品項產品之驗證※但經本公司重複驗證風險評估同意者，不在此限',
        '應以產銷履歷農產品作為主原料，除水、糖及食鹽外，其比例不得低於 50%',
        '其它',
    ],
};
/** 各版清單的最小 / 最大題數（TOP 12、TAP 14），供 zod 陣列長度校驗 */
export const CONTRACT_REVIEW_CHECKLIST_MIN = 12;
export const CONTRACT_REVIEW_CHECKLIST_MAX = 14;
/**
 * 依驗證方案取對應檢查項目清單。
 * TAP 用 TAP 版（14 項）；TOP 用 TOP 版（12 項）；ECO 暫無獨立表樣，回落 TOP。
 */
export function getContractReviewChecklist(scheme) {
    return scheme === 'TAP' ? CONTRACT_REVIEW_CHECKLISTS.TAP : CONTRACT_REVIEW_CHECKLISTS.TOP;
}
/**
 * 「確認驗證範圍」中的【驗證依據】固定文本 / 預設帶入值（序 58 需求）。
 *
 * - **TOP（有機）**：驗證依據為法定固定全文，直接以原文全文帶入，僅供只讀展示。
 * - **TAP（產銷履歷）**：驗證依據含 OOOO（良好農業規範版本）需手動錄入 / 由後台提取
 *   最新法規版本；此常量為預設帶入的範例值，草稿階段可由管理組 / 組長編輯覆蓋。
 *
 * ⚠️ TODO：TAP 的良好農業規範版本（TGAP 版次）後續應由後台「最新法規版本」字典自動提取，
 *   目前先以範例值預填、允許人工修改。
 */
export const VERIFICATION_BASIS_TEXT = {
    TOP: '有機農業促進法、有機農產品有機轉型期農產品驗證基準與其生產加工分裝流通及販賣過程可使用之物質' +
        '第一章第一部分、第二部分、第三部分、以及第二章一、二、中華驗證有限驗證公開文件及相關法規、' +
        '行政規則、公告與相關函釋。',
    // TAP：OOOO 處為良好農業規範名稱與版次，預設帶入範例（水稻臺灣良好農業規範(TGAP)110.1.29版），可人工修改
    TAP: '農產品生產及驗證管理法、產銷履歷農產品驗證基準、水稻臺灣良好農業規範(TGAP)110.1.29版',
};
/**
 * 依驗證方案取【驗證依據】預設文本（TAP 用 TAP 範例、其餘回落 TOP 固定全文）。
 */
export function getVerificationBasisText(scheme) {
    return scheme === 'TAP' ? VERIFICATION_BASIS_TEXT.TAP : VERIFICATION_BASIS_TEXT.TOP;
}
/**
 * @deprecated 舊單一 12 項常量，現為 TOP 版別名；新代碼請用
 * `CONTRACT_REVIEW_CHECKLISTS` / `getContractReviewChecklist(scheme)`。
 */
export const CONTRACT_REVIEW_CHECKLIST_LABELS = CONTRACT_REVIEW_CHECKLISTS.TOP;
/** 單項檢查的 schema（no 上限跟隨最大版清單題數） */
export const contractReviewChecklistItemSchema = z.object({
    no: z.number().int().min(1).max(CONTRACT_REVIEW_CHECKLIST_MAX),
    label: z.string(),
    answer: z.enum(['yes', 'no']),
    note: z.string().optional(),
});
/** PATCH（draft 階段編輯）；checklist 長度按 scheme 分版（TOP 12 / TAP 14） */
export const updateContractReviewSchema = z.object({
    checklist: z
        .array(contractReviewChecklistItemSchema)
        .min(CONTRACT_REVIEW_CHECKLIST_MIN)
        .max(CONTRACT_REVIEW_CHECKLIST_MAX)
        .optional(),
    leadOpinion: z.string().optional(),
    auditLanguage: z.string().min(1).optional(),
    /**
     * 【驗證依據】文本（序 58）——僅 TAP 允許人工修改（OOOO 良好農業規範版本），
     * TOP 為固定全文不編輯。傳入時 service 會合併回 scopeSnapshot.verificationBasis（jsonb）。
     * 長度上限寬鬆給到 2000（含法規全文條列）。
     */
    verificationBasis: z.string().max(2000).optional(),
    version: z.number().int().nonnegative(),
});
/**
 * 管理組初審（需求 B，客戶 PPT S22）。
 *
 * 初審不是狀態機新節點，用 adminReviewedAt 時間戳表達「申請文件齊全確認」；
 * note 可選（初審備註）。初審完成前組長不得 lead-submit（ADMIN_REVIEW_REQUIRED）。
 */
export const adminReviewContractReviewSchema = z.object({
    note: z.string().max(500).optional(),
});
/**
 * 組長提交（draft → lead_submitted）。
 *
 * 業務 refine：
 *   - 全部題目有 answer（由 z.array min/max 保證題數在版內）
 *   - 題 7（特殊要求）與最後一項「其它」選 yes 時必須填 note
 *     （「其它」在 TOP 版為第 12 題、TAP 版為第 14 題，統一取末位項校驗）
 */
export const leadSubmitContractReviewSchema = z
    .object({
    checklist: z
        .array(contractReviewChecklistItemSchema)
        .min(CONTRACT_REVIEW_CHECKLIST_MIN)
        .max(CONTRACT_REVIEW_CHECKLIST_MAX),
    /**
     * 驗證組長意見（序159 放寬）：受理（accept_taf）時不必填；不受理（reject）時承載原因、必填。
     * 由下方 refine 依 leadDecision 條件校驗。
     */
    leadOpinion: z.string().default(''),
    /**
     * 驗證組長決議（序59.4）：
     *   - accept_taf：受理，發 TAF 認證標誌證書
     *   - reject：不受理（leadOpinion 存原因）
     * optional 以免破壞既有測試（存量提交不帶此欄）；前端必填二選一。
     */
    leadDecision: z.enum(['accept_taf', 'reject']).optional(),
    /**
     * 驗證組長簽名檔 file id（序59.5：簽名設為**非必填**；可導入員工簽名檔或手寫）。
     */
    leadSignatureFileId: z.number().int().positive().optional(),
    /**
     * 簽名檔實際歸屬人 user id（序162：管理組代組長導入簽名檔時，
     * leadSignatureFileId 來自「組長本人」的員工檔案簽名檔，但提交人（leadUserId）
     * 是實際操作的管理組人員，兩者不同——此欄位記錄簽名真正屬於誰，供稽核追溯。
     * 與提交人相同（自己導入自己的簽名）或未使用員工簽名檔時可不傳。
     */
    leadSignatureOwnerId: z.number().int().positive().optional(),
    /**
     * 組長簽名日期（序59.4：預設當日、日期可修改；YYYY-MM-DD）。
     */
    leadSignDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD')
        .optional(),
    /**
     * 稽核使用語言（序59.3）：預設「中文」，可傳自訂文字（如「其他：英文」）。
     * 放寬為任意非空字串以承載「其他：______」自訂值。
     */
    auditLanguage: z.string().min(1).default('中文'),
    version: z.number().int().nonnegative(),
})
    .refine((v) => {
    // 題 7（序108 新語義）：「是」＝沒有特殊要求；「否」＝有特殊要求，必須說明
    const q7 = v.checklist.find((i) => i.no === 7);
    if (q7?.answer === 'no' && !q7.note?.trim())
        return false;
    // 「其它」兜底項固定在清單末位（TOP=12 / TAP=14），選「是」須填說明
    const other = v.checklist[v.checklist.length - 1];
    if (other?.answer === 'yes' && !other.note?.trim())
        return false;
    return true;
}, { message: '題 7 選「否（有特殊要求）」或「其它」選「是」時必須填說明' })
    // 序159：不受理（reject）時 leadOpinion 承載原因、必填；受理時免填
    .refine((v) => !(v.leadDecision === 'reject' && !v.leadOpinion.trim()), {
    message: '不受理時請填寫不受理原因',
    path: ['leadOpinion'],
});
/**
 * 總經理批示（lead_submitted → manager_approved / rejected）。
 *
 * 業務 refine：decision !== accept 時必須填 rejectionReason。
 */
export const managerDecideContractReviewSchema = z
    .object({
    decision: z.enum(['accept', 'reject_fixable', 'reject_terminal']),
    /** 總經理批示意見（序60：批示＝直接簽名，意見改為選填，預設空） */
    managerOpinion: z.string().optional(),
    /** 總經理簽名檔（序60.3：簽名設為**非必填**；可導入員工簽名檔） */
    managerSignatureFileId: z.number().int().positive().optional(),
    /** 總經理簽名日期（序60.1：預設當日、日期可修改；YYYY-MM-DD） */
    gmSignDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD')
        .optional(),
    rejectionReason: z.string().optional(),
    version: z.number().int().nonnegative(),
})
    .refine((v) => v.decision === 'accept' || !!v.rejectionReason?.trim(), {
    message: '不受理時必須填拒絕原因',
});
/**
 * 已通過合約評審的簽名資料補正。
 *
 * 僅管理組可在「現場稽核開始前」使用；reason 為必填的稽核軌跡，
 * signatureFileId / signDate 至少須提供一項，避免產生無實質變更的紀錄。
 */
export const correctContractReviewSignatureSchema = z
    .object({
    target: z.enum(['lead', 'manager']),
    signatureFileId: z.number().int().positive().optional(),
    signDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式須為 YYYY-MM-DD')
        .optional(),
    reason: z.string().trim().min(1, '請填寫補正原因').max(500),
    version: z.number().int().nonnegative(),
})
    .refine((value) => value.signatureFileId !== undefined || value.signDate !== undefined, {
    message: '簽名檔與簽名日期至少須補正一項',
});
/**
 * 「驗證組長簽名人選」候選項（序162）：供管理組協助組長提交合約評審時，
 * 選擇要導入哪一位組長／組長代理人的員工檔案簽名檔，而非只能導入自己的簽名檔。
 * 候選來源為具備 role='verification_lead' 的在職用戶（見 GET /api/contract-reviews/lead-signature-options）。
 */
export const leadSignatureCandidateDtoSchema = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    signatureFileId: z.number().int().positive().nullable(),
});
/**
 * 合約評審表的表單模板系統識別鍵（2026-09-25 回報序76）。
 *
 * 稽核文件包中的「有機合約評審表」（B-ZHSR01-12）/「合約評審表」（產銷履歷 B-ZHSR01-3）
 * 表單實例，內容一律**同步自**派案專區的合約評審（contract_reviews），不需重新輸入；
 * 前端據此把這兩張表單顯示為唯讀並提示資料來源。
 */
export const CONTRACT_REVIEW_FORM_TEMPLATE_KEYS = ['B-ZHSR01-12', 'B-ZHSR01-3'];
